import { Routes } from '@angular/router';

import { LoginComponent } from './features/login/login.component';
import { SignupComponent } from './features/signup/signup.component';
import { LayoutComponent } from './layout/layout.component';
import { DashboardComponent } from './features/dashboard/dashboard.component';
import { PreventLoginAccessGuard } from './core/guards/prevent-login-access.guard';
import { DynamicAuthGuard } from './core/guards/dynamic-auth.guard';
import { AbsolutePermeabilityComponent } from './features/simulators/absolute-permeability/absolute-permeability.component';
import { SimulationsSearchComponent } from './features/simulations-search/simulations-search.component';
import { AppLevelInfoResolver } from './core/resolvers/app-level-info.resolver';
import { FormationFactorComponent } from './features/simulators/formation-factor/formation-factor.component';
import { CapillaryPressureComponent } from './features/simulators/capillary-pressure/capillary-pressure.component';
import { RelativePermeabilityComponent } from './features/simulators/relative-permeability/relative-permeability.component';
import { KabsSimulationResultsComponent } from './common/simulation-results/kabs-simulation-results/kabs-simulation-results.component';
import { PoreThroatSizeDistributionsComponent } from './features/simulators/pore-throat-size-distributions/pore-throat-size-distributions.component';
import { PtsdSimulationResultsComponent } from './common/simulation-results/ptsd-simulation-results/ptsd-simulation-results.component';
import { UpscalingAbsolutePermeabilityComponent } from './features/simulators/upscaling/absolute-permeability/absolute-permeability.component';
import { FormationFactorSimulationResultsComponent } from './common/simulation-results/formation-factor-simulation-results/formation-factor-simulation-results.component';
import { CubeTrendAbsolutePermeabilityComponent } from './features/simulators/cube-trend-absolute-permeability/cube-trend-absolute-permeability.component';
import { UpscalingFormationFactorComponent } from './features/simulators/upscaling/formation-factor/formation-factor.component';
import { UpscalingCapillaryPressureComponent } from './features/simulators/upscaling/capillary-pressure/capillary-pressure.component';
import { UpscalingRelativePermeabilityComponent } from './features/simulators/upscaling/relative-permeability/relative-permeability.component';
import { UpscalingSaturationExponentComponent } from './features/simulators/upscaling/saturation-exponent/saturation-exponent.component';
import { CubeTrendFormationFactorComponent } from './features/simulators/cube-trend-formation-factor/cube-trend-formation-factor.component';
import { OktaCallbackComponent } from '@okta/okta-angular';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { AuthCallbackComponent } from './features/login/auth-callback.component';
import { RockImagesComponent } from './features/rock-images/rock-images.component';
import { CapillaryPressureResultsComponent } from './common/simulation-results/capillary-pressure-results/capillary-pressure-results.component';
import { SaturationExponentComponent } from './features/simulators/saturation-exponent/saturation-exponent.component';
import { CubeTrendFormationFactorResultsComponent } from './common/simulation-results/cube-trend-formation-factor-results/cube-trend-formation-factor-results.component';
import { RelativePermeabilityResultsComponent } from './common/simulation-results/relative-permeability-results/relative-permeability-results.component';
import { SaturationExponentResultsComponent } from './common/simulation-results/saturation-exponent-results/saturation-exponent-results.component';

import { CubeTrendAbsolutePermeabilityResultsComponent } from './common/simulation-results/cube-trend-absolute-permeability-results/cube-trend-absolute-permeability-results.component';
import { UpscalingRockImagesComponent } from './features/upscaling-rock-images/upscaling-rock-images.component';
import { RockModelsComponent } from './features/rock-models/rock-models.component';
import { PhaseDefinitionComponent } from './features/phase-definition/phase-definition.component';
import { UpscalingFluidFlowModelsComponent } from './features/upscaling-fluid-flow-models/upscaling-fluid-flow-models.component';
import { FluidFlowManagerComponent } from './features/fluid-flow-manager/fluid-flow-manager.component';
import { FluidFlowDefinitionComponent } from './features/fluid-flow-definition/fluid-flow-definition.component';
export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [PreventLoginAccessGuard],
  },
  {
    path: 'signup',
    component: SignupComponent,
    canMatch: [() => false],
  },

  // Unified auth redirect callback – handles both Okta and MSAL on the same
  // path since both providers are registered with /login/callback in their
  // respective IdP app registrations.
  {
    path: 'login/callback',
    component: AuthCallbackComponent,
  },

  {
    path: '',
    component: LayoutComponent,
    resolve: { appLevelInfo: AppLevelInfoResolver },
    canActivate: [DynamicAuthGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', component: DashboardComponent },
      { path: 'simulations-search', component: SimulationsSearchComponent },
      { path: 'absolute-permeability', component: AbsolutePermeabilityComponent },
      { path: 'pore-throat-size-distributions', component: PoreThroatSizeDistributionsComponent },
      { path: 'formation-factor', component: FormationFactorComponent },
      { path: 'capillary-pressure', component: CapillaryPressureComponent },
      { path: 'relative-permeability', component: RelativePermeabilityComponent },
      { path: 'saturation-exponent', component: SaturationExponentComponent },
      { path: 'rock-images', component: RockImagesComponent },
      { path: 'rock-models', component: RockModelsComponent },
      { path: 'phase-definition', component: PhaseDefinitionComponent },
      {
        path: 'simulation-results',
        children: [
          { path: 'kabs/:id', component: KabsSimulationResultsComponent },
          { path: 'ptsd/:id', component: PtsdSimulationResultsComponent },
          { path: 'form-factor/:id', component: FormationFactorSimulationResultsComponent },
          { path: 'pc/:id', component: CapillaryPressureResultsComponent },
          { path: 'kr/:id', component: RelativePermeabilityResultsComponent },
          { path: 'n/:id', component: SaturationExponentResultsComponent },
          { path: 'cube-trend/formation-factor/:id', component: CubeTrendFormationFactorResultsComponent },
          { path: 'cube-trend/absolute-permeability/:id', component: CubeTrendAbsolutePermeabilityResultsComponent },
        ],
      },
      {
        path: 'upscaling',
        children: [
          { path: 'rock-images', component: UpscalingRockImagesComponent },
          { path: 'absolute-permeability', component: UpscalingAbsolutePermeabilityComponent },
          { path: 'formation-factor', component: UpscalingFormationFactorComponent },
          { path: 'capillary-pressure', component: UpscalingCapillaryPressureComponent },
          { path: 'relative-permeability', component: UpscalingRelativePermeabilityComponent },
          { path: 'saturation-exponent', component: UpscalingSaturationExponentComponent },
          { path: 'fluid-flow-models', component: UpscalingFluidFlowModelsComponent },
          { path: 'fluid-flow-manager', component: FluidFlowManagerComponent },
          { path: 'fluid-flow-definition', component: FluidFlowDefinitionComponent },
        ],
      },
      {
        path: 'cube-trend',
        children: [
          { path: 'absolute-permeability', component: CubeTrendAbsolutePermeabilityComponent },
          { path: 'formation-factor', component: CubeTrendFormationFactorComponent },
        ],
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
